import React, { useState } from 'react';
import {
  Modal,
  Button,
  Card,
  Row,
  Col,
  Typography,
  Tag,
  Alert,
  Space,
  Tabs
} from 'antd';
import { WhatsAppOutlined, EditOutlined } from '@ant-design/icons';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import EditCampaignMessageForm from './EditCampaignMessageForm';
import { getStatusColor, getStatusLabel } from '../../utils/campaignUtils';

const { Title, Text } = Typography;
const { TabPane } = Tabs;



const CampaignMessageModal = ({ message, visible, onClose, onMessageUpdated }) => {
  const [activeTab, setActiveTab] = useState('view');
  const [currentMessage, setCurrentMessage] = useState(null);

  // Update current message when the input message changes
  React.useEffect(() => {
    if (message) {
      setCurrentMessage(message);
    }
  }, [message]);

  // Reset to view tab when modal closes
  React.useEffect(() => {
    if (!visible) {
      setActiveTab('view');
    }
  }, [visible]);

  if (!currentMessage) return null;

  const formatDateTime = (dateString) => {
    if (!dateString) return '-';
    return format(new Date(dateString), 'dd/MM/yyyy HH:mm', { locale: ptBR });
  };

  // Render product details consistently
  const renderFavoriteProduct = (product) => {
    if (!product) return null;
    
    if (typeof product === 'object') {
      return (
        <div>
          <div><strong>Nome:</strong> {product.name}</div>
          {product.reference && 
            <div><strong>Referência:</strong> {product.reference}</div>
          }
          
          {product.quantity && 
            <div><strong>Quantidade:</strong> {product.quantity}</div>
          }
        </div>
      );
    }
    
    return product;
  };

  // Handle successful message update
  const handleMessageUpdated = (updatedMessage) => {
    setCurrentMessage({
      ...currentMessage,
      message: updatedMessage.message,
      updatedAt: updatedMessage.updatedAt
    });
    setActiveTab('view');
    
    // Notify parent component if provided
    if (onMessageUpdated) {
      onMessageUpdated(updatedMessage);
    }
  };

  // View content
  const viewContent = (
    <Card>
      <Row gutter={[16, 16]}>
        <Col span={24}>
          <Title level={5}>Cliente</Title>
          <Text>{currentMessage.customer?.name}</Text>
        </Col>

        <Col span={12}>
          <Title level={5}>WhatsApp</Title>
          <Text>{currentMessage.customer?.whatsapp}</Text>
        </Col>

        <Col span={12}>
          <Title level={5}>Status</Title>
          <Tag color={getStatusColor(currentMessage.status)}>
            {getStatusLabel(currentMessage.status)}
          </Tag>
        </Col>

        {currentMessage.customer?.document && (
          <Col span={24}>
            <Title level={5}>Documento</Title>
            <Text>{currentMessage.customer?.document}</Text>
          </Col>
        )}

        <Col span={24}>
          <Title level={5}>Mensagem</Title>
          <Card
            style={{
              backgroundColor: '#dcf8c6',
              borderRadius: '12px',
              padding: '12px',
              whiteSpace: 'pre-wrap'
            }}
          >
            <div style={{ whiteSpace: 'pre-wrap', wordWrap: 'break-word' }}>
              {currentMessage.message}
            </div>
          </Card>
        </Col>

        {currentMessage.favoriteProduct && (
          <Col span={24}>
            <Title level={5}>Produto Favorito</Title>
            {renderFavoriteProduct(currentMessage.favoriteProduct)}
          </Col>
        )}

        {currentMessage.scheduledFor && (
          <Col span={24}>
            <Title level={5}>Agendado para</Title>
            <Text>{formatDateTime(currentMessage.scheduledFor)}</Text>
          </Col>
        )}

        {currentMessage.errorMessage && (
          <Col span={24}>
            <Title level={5}>Mensagem de Erro</Title>
            <Alert type="error" message={currentMessage.errorMessage} />
          </Col>
        )}

        <Col span={24}>
          <Title level={5}>Criado em</Title>
          <Text>{formatDateTime(currentMessage.createdAt)}</Text>
        </Col>
        
    
      </Row>
    </Card>
  );

  // Edit content
  const editContent = (
    <EditCampaignMessageForm 
      message={currentMessage} 
      onSuccess={handleMessageUpdated}
      onCancel={() => setActiveTab('view')}
    />
  );

  // Determine if edit tab should be visible (only for pending messages)
  const showEditTab = currentMessage.status === 'pending';
  
  // If in edit mode, show the tabs
  const renderContent = () => {
    if (showEditTab) {
      return (
        <Tabs activeKey={activeTab} onChange={setActiveTab}>
          <TabPane tab="Visualizar" key="view">
            {viewContent}
          </TabPane>
          {currentMessage.status === 'pending' && (
            <TabPane tab="Editar" key="edit">
              {editContent}
            </TabPane>
          )}
        </Tabs>
      );
    }
    
    // Otherwise just show the view content
    return viewContent;
  };

  return (
    <Modal
      title={
        <Space>
          <WhatsAppOutlined />
          <span>Mensagem da Campanha</span>
        </Space>
      }
      open={visible}
      onCancel={onClose}
      footer={[
        <Button key="close" onClick={onClose}>
          Fechar
        </Button>
      ]}
      width={700}
    >
      {renderContent()}
    </Modal>
  );
};

export default CampaignMessageModal; 